import { AlertCircle, ArrowRight, Clock } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/page-hero";
import { Reveal } from "@/lib/motion";
import { IntakeForm } from "@/components/intake-form";
import { SITE, whatsappHref, CTAS } from "@/content/site";
import { CtaLink } from "@/components/cta";

export default function AtencionPrioritaria() {
  return (
    <>
      <Seo
        title="Atención prioritaria"
        description="¿Tienes una notificación, una fecha próxima o un asunto urgente? Entra por la ruta prioritaria de Tulevik Legal. Sin promesas 24/7, con respuesta en horario hábil."
        path="/atencion-prioritaria"
      />
      <PageHero
        eyebrow="Atención prioritaria"
        titulo="¿Hay una notificación o una fecha encima de la mesa?"
        lead="Si tu asunto tiene plazo, audiencia o embargo encima, no lo dejes correr. Esta ruta es más corta: nos dices qué recibiste y cuándo vence, y lo atendemos con prioridad."
      />

      <section className="bg-parchment">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="border border-hairline bg-ink-deep p-8 text-parchment" data-testid="prioritaria-whatsapp-panel">
                  <p className="eyebrow mb-5 text-salvia-marco">Vía rápida · WhatsApp</p>
                  <h2 className="font-serif text-3xl leading-tight">Lo más rápido: escribirnos directo</h2>
                  <p className="mt-4 text-sm leading-relaxed text-parchment/70">
                    Envíanos un mensaje con lo esencial: qué recibiste, qué fecha vence y dónde estás.
                    Revisamos y te respondemos por el mismo medio.
                  </p>
                  <a
                    href={whatsappHref(
                      "Hola, tengo un asunto con fecha próxima o notificación y necesito atención prioritaria."
                    )}
                    target={SITE.WHATSAPP_NUMERO ? "_blank" : undefined}
                    rel={SITE.WHATSAPP_NUMERO ? "noopener noreferrer" : undefined}
                    data-testid="prioritaria-whatsapp-button"
                    className="btn-primary mt-8 w-full"
                  >
                    {CTAS.secundaria.label}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                  <div className="mt-8 space-y-4 border-t border-parchment/20 pt-6 text-sm text-parchment/70">
                    <p className="flex items-start gap-3">
                      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-azul" />
                      Respondemos en horario hábil, de lunes a viernes. No ofrecemos atención 24/7.
                    </p>
                    <p className="flex items-start gap-3">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-azul" />
                      Si escribiste fuera de horario, te respondemos al inicio del siguiente día hábil.
                    </p>
                    <p className="flex items-start gap-3">
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-azul" />
                      Respetar tus plazos es lo primero: mientras te respondemos, no dejes pasar
                      fechas oficiales que no dependan de nosotros.
                    </p>
                  </div>
                </div>

                <p className="mt-6 text-sm leading-relaxed text-ink-muted">
                  ¿Tu asunto no es urgente? Usa el{" "}
                  <a href="/contacto" data-testid="prioritaria-contacto-link" className="font-medium text-azul underline underline-offset-4">
                    formulario normal de contacto
                  </a>
                  . Así clasificamos mejor y atendemos más rápido a quien lo necesita de verdad.
                </p>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={0.1}>
                <IntakeForm variante="prioritario" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
